import React, { Component, useState } from 'react';
import { bool, func, node, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { FieldHelp } from '../../components';
import classNames from 'classnames';

import css from './FieldPasswordInput.module.css';

const FieldPasswordInputComponent = props => {
  const [isVisible, setIsVisible] = useState(false);
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    intl,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    helpText,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const helpId = id ? `${id}-help` : null;
  const showError = hasError && !hideErrorMessage;
  const a11yProps = {
    'aria-invalid': hasError ? 'true' : undefined,
    'aria-describedby': (showError || helpText) && helpId ? helpId : undefined,
  };

  // Uncontrolled input uses defaultValue instead of value.
  const { type, ...inputWithoutType } = input;
  const { value: defaultValue, ...inputWithoutValue } = inputWithoutType;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
    });
  const inputProps = isUncontrolled
    ? {
        className: inputClasses,
        id,
        type: isVisible ? 'text' : 'password',
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
        ...a11yProps,
      }
    : {
        className: inputClasses,
        id,
        type: isVisible ? 'text' : 'password',
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
        ...a11yProps,
      };
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      <div className={css.inputWrapper}>
        <input {...inputProps} />
        <button
          className={css.toggle}
          type="button"
          onClick={() => setIsVisible(!isVisible)}
          aria-label={
            isVisible
              ? intl.formatMessage({ id: 'FieldPasswordInput.hidePassword' })
              : intl.formatMessage({ id: 'FieldPasswordInput.showPassword' })
          }
        >
          <FormattedMessage
            id={isVisible ? 'FieldPasswordInput.hide' : 'FieldPasswordInput.show'}
          />
        </button>
      </div>
      <FieldHelp id={helpId} helpText={helpText} errorText={showError ? errorText : null} />
    </div>
  );
};

FieldPasswordInputComponent.defaultProps = {
  helpText: null,
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
};

FieldPasswordInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,
  // The line under the field, e.g. "At least 8 characters."
  helpText: node,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    type: string,
  }).isRequired,
  meta: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

class FieldPasswordInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldPasswordInputComponent} {...this.props} />;
  }
}

export default injectIntl(FieldPasswordInput);
